import { useState, useEffect, useCallback } from 'react';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import { exportTicketsPdf } from '../utils/exportTicketsPdf';

export const useTicketsReport = () => {
  const { token, idUsuario, nombreCompleto } = useToken();
  const { showErrorToast, showSuccessToast } = useToast();

  // Filtros principales (todos por defecto)
  const [statusFilter, setStatusFilter] = useState('todos'); // 'todos', '1', '3', '2', 'combinados'
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce del término de búsqueda hacia la base de datos
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Filtro de fechas (por defecto todas las fechas)
  const [dateFilter, setDateFilter] = useState({
    mode: 'todos',
    startDate: null,
    endDate: null,
    dFechaInicio: null,
    dFechaFin: null
  });

  // Paginación y datos
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [tickets, setTickets] = useState([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [counts, setCounts] = useState({ todos: 0, pendientes: 0, enProgreso: 0, cerrados: 0, combinados: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Cargar lista de agentes
  useEffect(() => {
    if (!token) return;
    const fetchAgents = async () => {
      setIsLoadingAgents(true);
      try {
        const response = await fetch('/api/SupportEngine/GetAgentsList', {
          headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
        });
        if (response.ok) {
          const result = await response.json();
          const data = result.data || (Array.isArray(result) ? result : []);
          setAgents(Array.isArray(data) ? data : []);
        }
      } catch {
        // Silencioso
      } finally {
        setIsLoadingAgents(false);
      }
    };
    fetchAgents();
  }, [token]);

  // Consulta de tickets para el reporte directo a base de datos
  const fetchReportData = useCallback(async (targetPage = page, targetPageSize = pageSize) => {
    if (!token) return;
    setIsLoading(true);

    try {
      const params = new URLSearchParams();
      if (statusFilter === 'combinados') {
        params.append('bIsCombinado', 'true');
      } else if (statusFilter && statusFilter !== 'todos') {
        params.append('iEstadoTicket', statusFilter);
      }
      if (selectedAgentId && selectedAgentId !== 'todos' && selectedAgentId !== 'all') {
        params.append('idUsuarioAsignado', selectedAgentId);
      }
      if (dateFilter.dFechaInicio) {
        params.append('dFechaInicio', dateFilter.dFechaInicio);
      }
      if (dateFilter.dFechaFin) {
        params.append('dFechaFin', dateFilter.dFechaFin);
      }
      if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
        params.append('searchTerm', debouncedSearchTerm.trim());
      }
      params.append('pageNumber', targetPage);
      params.append('pageSize', targetPageSize);

      const response = await fetch(`/api/SupportEngine/GetTicketsReport?${params.toString()}`, {
        headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
      });

      if (!response.ok) {
        throw new Error('Error al cargar reporte de tickets');
      }

      const result = await response.json();
      const data = result.data || result.tickets || (Array.isArray(result) ? result : []);
      const ticketList = Array.isArray(data) ? data : [];

      setTickets(ticketList);

      const totalCount = ticketList.length > 0 && ticketList[0].iTotalRegistros != null
        ? Number(ticketList[0].iTotalRegistros)
        : ticketList.length;
      setTotalRegistros(totalCount);

      const countsData = result.counts || (result.data && !Array.isArray(result.data) ? result.data.counts : null);
      if (countsData) {
        setCounts({
          todos: Number(countsData.totalAll ?? countsData.iTotalTodos ?? 0),
          pendientes: Number(countsData.totalPending ?? countsData.iTotalPendientes ?? 0),
          enProgreso: Number(countsData.totalInProgress ?? countsData.iTotalEnProgreso ?? 0),
          cerrados: Number(countsData.totalClosed ?? countsData.iTotalCerrados ?? 0),
          combinados: Number(countsData.totalCombined ?? countsData.iTotalCombinados ?? 0)
        });
      }
    } catch (err) {
      if (showErrorToast) {
        showErrorToast('Error', err.message || 'No se pudo cargar el reporte');
      }
    } finally {
      setIsLoading(false);
    }
  }, [token, statusFilter, selectedAgentId, dateFilter, debouncedSearchTerm, page, pageSize, showErrorToast]);

  // Carga reactiva ante cambios de filtros o búsqueda debounced
  useEffect(() => {
    fetchReportData(page, pageSize);
  }, [fetchReportData, page, pageSize]);

  // Manejadores de filtros
  const handleStatusChange = (status) => {
    setStatusFilter(status);
    setPage(1);
  };

  const handleDateChange = (newDateFilter) => {
    setDateFilter(newDateFilter);
    setPage(1);
  };

  const handleAgentChange = (agentId) => {
    setSelectedAgentId(agentId);
    setPage(1);
  };

  // Exportar reporte completo a formato PDF
  const handleExportPdf = async () => {
    if (tickets.length === 0) {
      if (showErrorToast) showErrorToast('Sin datos', 'No hay tickets para exportar con los filtros seleccionados.');
      return;
    }

    setIsExporting(true);
    try {
      // Consultar lista completa correspondiente al filtro actual (hasta 2000 registros para PDF)
      const params = new URLSearchParams();
      if (statusFilter === 'combinados') {
        params.append('bIsCombinado', 'true');
      } else if (statusFilter && statusFilter !== 'todos') {
        params.append('iEstadoTicket', statusFilter);
      }
      if (selectedAgentId && selectedAgentId !== 'todos' && selectedAgentId !== 'all') {
        params.append('idUsuarioAsignado', selectedAgentId);
      }
      if (dateFilter.dFechaInicio) params.append('dFechaInicio', dateFilter.dFechaInicio);
      if (dateFilter.dFechaFin) params.append('dFechaFin', dateFilter.dFechaFin);
      if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
        params.append('searchTerm', debouncedSearchTerm.trim());
      }
      params.append('pageNumber', 1);
      params.append('pageSize', 2000);

      const response = await fetch(`/api/SupportEngine/GetTicketsReport?${params.toString()}`, {
        headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
      });

      let exportList = tickets;
      if (response.ok) {
        const result = await response.json();
        const data = result.data || result.tickets || [];
        if (Array.isArray(data) && data.length > 0) {
          exportList = data;
        }
      }

      exportTicketsPdf({
        tickets: exportList,
        statusFilter,
        selectedAgentId,
        dateFilter,
        agents
      });

      if (showSuccessToast) {
        showSuccessToast('Exportación PDF completa', `Se descargó el reporte en PDF con ${exportList.length} tickets.`);
      }
    } catch (err) {
      if (showErrorToast) {
        showErrorToast('Error al exportar PDF', err.message);
      }
    } finally {
      setIsExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalRegistros / pageSize));

  return {
    tickets,
    filteredTickets: tickets,
    totalRegistros,
    totalPages,
    counts,
    isLoading,
    isExporting,
    statusFilter,
    selectedAgentId,
    dateFilter,
    searchTerm,
    setSearchTerm,
    page,
    setPage,
    pageSize,
    setPageSize,
    agents,
    isLoadingAgents,
    idUsuario,
    nombreCompleto,
    handleStatusChange,
    handleDateChange,
    handleAgentChange,
    handleExportPdf
  };
};

export default useTicketsReport;
